import { Group } from 'src/design/atoms/Group';
import { Paper } from 'src/design/atoms/Paper';
import { Stack } from 'src/design/atoms/Stack';
import { Text } from 'src/design/atoms/Text';
import { IconX } from '@tabler/icons-react';
import React, { useMemo } from 'react';
import { AttachmentDto, PostDocumentDto } from 'src/api/FiretrolApiClient';
import { useResolveAttachmentUrl } from 'src/api/queries/attachments/useAttachmentMutations';
import { useDownloadDocument } from 'src/api/queries/documents/useDocumentDownload';
import { humanFileSize } from 'src/utils';
import { BasicActionIcon } from 'src/design/atoms/BasicActionIcon';
import { BasicButton } from 'src/design/atoms/BasicButton';
import { fileIcon } from 'src/app/documents/fileIcons';

interface PostFilesProps {
  files: AttachmentDto[];
  documents: PostDocumentDto[];
  onFileRemoved: (file: AttachmentDto) => void;
  onDocumentRemoved: (doc: PostDocumentDto) => void;
  jobId?: number;
}

const acceptedImageTypes = ['image/jpeg', 'image/png', 'image/gif'];

export const PostFiles = React.memo<PostFilesProps>(
  ({ files, documents, onFileRemoved, onDocumentRemoved, jobId }) => {
    const downloadAttachment = useResolveAttachmentUrl();
    const downloadDocument = useDownloadDocument();

    const images = useMemo(() => {
      return files.filter(
        (f) => acceptedImageTypes.indexOf(f.subtype ?? '') >= 0,
      );
    }, [files]);

    const nonImages = useMemo(() => {
      return files.filter(
        (f) => acceptedImageTypes.indexOf(f.subtype ?? '') < 0,
      );
    }, [files]);

    const downloadFile = (id: string) => downloadAttachment.mutate(id);

    const downloadDoc = (doc: PostDocumentDto) => {
      const documentJobId = doc.jobId ?? jobId;
      if (!documentJobId) return;
      downloadDocument.mutate({ jobId: documentJobId, documentId: doc.id });
    };

    if (files.length === 0 && documents.length === 0) {
      return <></>;
    }

    return (
      <Stack gap="sm">
        {documents.map((doc) => (
          <Paper key={doc.id} withBorder p="xs">
            <Group justify="space-between" wrap="nowrap">
              <Group gap="xs" wrap="nowrap">
                {fileIcon(doc.type)}
                <Text tt="uppercase">Document</Text>
                <BasicButton
                  variant="subtle"
                  size="sm"
                  onClick={() => downloadDoc(doc)}
                >
                  {`${doc.name}.${doc.type}`}
                </BasicButton>
                <Text size="sm" c="dimmed">
                  {humanFileSize(doc.size ?? 0)}
                </Text>
              </Group>
              <BasicActionIcon
                variant="subtle"
                color="red"
                onClick={() => onDocumentRemoved(doc)}
                size="sm"
              >
                <IconX size={16} />
              </BasicActionIcon>
            </Group>
          </Paper>
        ))}

        {nonImages.map((file) => (
          <Paper key={file.id} withBorder p="xs">
            <Group justify="space-between" wrap="nowrap">
              <Group gap="xs" wrap="nowrap">
                {fileIcon(file.type)}
                <Text tt="uppercase">File</Text>
                <BasicButton
                  variant="subtle"
                  size="sm"
                  onClick={() => downloadFile(file.id)}
                >
                  {file.name}
                </BasicButton>
                <Text size="sm" c="dimmed">
                  {humanFileSize(file.size)}
                </Text>
              </Group>
              <BasicActionIcon
                variant="subtle"
                color="red"
                onClick={() => onFileRemoved(file)}
                size="sm"
              >
                <IconX size={16} />
              </BasicActionIcon>
            </Group>
          </Paper>
        ))}

        {images.length > 0 && (
          <Paper withBorder p="xs">
            <Stack gap="xs">
              {images.map((image) => (
                <Group key={image.id} justify="space-between" wrap="nowrap">
                  <Group gap="xs" wrap="nowrap">
                    {fileIcon(image.type)}
                    <Text tt="uppercase">Image</Text>
                    <BasicButton
                      variant="subtle"
                      size="sm"
                      onClick={() => downloadFile(image.id)}
                    >
                      {image.name}
                    </BasicButton>
                    <Text size="sm" c="dimmed">
                      {humanFileSize(image.size)}
                    </Text>
                  </Group>
                  <BasicActionIcon
                    variant="subtle"
                    color="red"
                    onClick={() => onFileRemoved(image)}
                    size="sm"
                  >
                    <IconX size={16} />
                  </BasicActionIcon>
                </Group>
              ))}
            </Stack>
          </Paper>
        )}
      </Stack>
    );
  },
);
